'use client';

import { type FC, useEffect } from 'react';
import {
  DIALOG_RETENTION_EXPIRING,
  DIALOG_RETENTION_LAST_DAY,
} from '@/app/components/shared/OpenScreenDialog/constants';
import { reportHomePopupClick } from '@/app/components/shared/OpenScreenDialog/utils';
import { Button } from '@/app/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/app/components/ui/dialog';
import { useStore } from '@/app/providers/store';
import { getSubscriptionUrl, reportEvent } from '@/app/utils';
import { useRouter } from '@/i18n/routing';
import { PLAN_BENEFITS_MAP } from '../constants';
import { useRetentionStrategy } from '../hooks/use-retention-strategy';
import { DiscountIcon } from '../icons/DiscountIcon';
import { FastGenerateIcon } from '../icons/FastGenerateIcon';
import { FreeGenerateIcon } from '../icons/FreeGenerateIcon';

interface RetentionOpenScreenDialogProps {
  open: boolean;
  onClose: () => void;
}

export const RetentionOpenScreenDialog: FC<RetentionOpenScreenDialogProps> = ({ open, onClose }) => {
  const { phase: retentionPhase, daysToExpiry } = useRetentionStrategy();
  const planType = useStore((s) => s.subscription?.plan_type);
  const router = useRouter();
  const isLastDay = retentionPhase === 'phase3';
  const days = Math.ceil(daysToExpiry);
  const benefits = planType ? PLAN_BENEFITS_MAP[planType] : undefined;
  const trackingType = isLastDay ? 'd1-splash' : 'd7-splash';

  useEffect(() => {
    if (open) {
      reportEvent('page_name', { subscription_reactivation: trackingType });
    }
  }, [open, trackingType]);

  const handleClick = () => {
    reportHomePopupClick(isLastDay ? DIALOG_RETENTION_LAST_DAY : DIALOG_RETENTION_EXPIRING);
    reportEvent('subscription_reactivation_click', { view: trackingType });
    router.push(getSubscriptionUrl(false, 'tab=plan&subscription-period=subscription_monthly'));
    onClose();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
    >
      <DialogContent
        className="w-[400px] gap-0 overflow-clip rounded-2xl border border-system-white12 bg-[#121316] p-0 shadow-[0px_20px_60px_0px_rgba(0,0,0,0.32)] sm:w-full md:max-w-[400px]"
        closable
      >
        <DialogHeader className="gap-0 p-0">
          <DialogTitle />
        </DialogHeader>

        <div className="flex flex-col items-center gap-4 px-8 pt-10">
          <div className="rounded-full bg-[#e05f3f] px-4 py-1 text-white text-xs leading-[1.6]">
            {isLastDay ? '今天到期，会员即将失效' : `套餐倒计时 ${days} 天`}
          </div>
          <div className="text-center font-semibold text-[32px] text-white leading-[1.2]">
            会员到期后将
            <br />
            降为免费版
          </div>
          <div className="text-center text-sm text-white/48 leading-[1.6]">
            限时续费或升级连续包月套餐享受专属权益：
          </div>

          <div className="flex w-full items-center gap-2 rounded-2xl border border-system-white04 bg-system-white/5 px-2 py-4">
            <div className="flex flex-1 flex-col items-center gap-1">
              <DiscountIcon className="size-12" />
              <div className="flex flex-col items-center text-center text-white">
                <span className="font-semibold text-sm leading-[1.6]">8折折扣</span>
                <span className="text-white/40 text-xs leading-[1.6]">多场景生成</span>
              </div>
            </div>

            <div className="h-20 w-px bg-system-white12" />

            <div className="flex flex-1 flex-col items-center gap-1">
              <FastGenerateIcon className="size-12" />
              <div className="flex flex-col items-center text-center text-white">
                <span className="font-semibold text-sm leading-[1.6]">{benefits?.channel ?? '快速'}生成</span>
                <span className="text-white/40 text-xs leading-[1.6]">表达更有戏</span>
              </div>
            </div>

            {benefits?.dailyFree && (
              <>
                <div className="h-20 w-px bg-system-white12" />
                <div className="flex flex-1 flex-col items-center gap-1">
                  <FreeGenerateIcon className="size-12" />
                  <div className="flex flex-col items-center text-center text-white">
                    <span className="font-semibold text-sm leading-[1.6]">{benefits.dailyFree}</span>
                    <span className="text-white/40 text-xs leading-[1.6]">免费生成</span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="px-8 pt-10 pb-8">
          <Button
            className="w-full overflow-hidden rounded-xl border border-white bg-gradient-to-r from-[#b2d7ff] to-[#ffe3da] py-3 font-semibold text-black text-sm shadow-[inset_0px_0px_10px_0px_white] hover:opacity-90"
            onClick={handleClick}
          >
            继续订阅
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
